import '../css/style.css';

import Headroom from 'headroom.js';

import Swiper from 'swiper';
import { Pagination, Navigation, Grid, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/autoplay';

import Alpine from 'alpinejs';
import intersect from '@alpinejs/intersect';
import collapse from '@alpinejs/collapse';
Alpine.plugin(collapse);
Alpine.plugin(intersect);

window.Alpine = Alpine;
// ponytail: siteData is injected via wp_add_inline_script('before'); guard covers misconfigured envs
window.siteData = typeof siteData !== 'undefined' ? siteData : {};

Alpine.data('siteMenu', () => ({
  latestTab(elm) {
    const tab = elm.dataset.tab;
    const targetTab = document.querySelector(
      `.latest-tab a[data-tab="${tab}"]`
    );
    if (!targetTab) return;
    targetTab.click();
  },
}));

// Header
Alpine.data('siteHeader', () => ({
  init() {
    this.headroom = new Headroom(this.$el);
    this.headroom.init();
  },
  destroy() {
    if (this.headroom) {
      this.headroom.destroy();
    }
  },
  showmenu: false,
  headroom: null,
}));

// Hero Banner Slider
Alpine.data('heroBanner', () => ({
  init() {
    const swiper = this.$el.querySelector('.swiper');

    if (!swiper) return;
    this.slider = new Swiper(swiper, {
      modules: [Autoplay],
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      speed: 1,
      loop: true,
      allowTouchMove: false,
    });
  },
  slider: null,
}));

Alpine.data('latestList', (tab = 'works') => ({
  tab: tab,
  target: null,
}));

// Shared accordion behavior for list items (project / story / idea).
// ponytail: _x_dataStack is an Alpine internal; upgrade path = Alpine.store or $dispatch events
function initSliders(root) {
  for (const attr of ['singleSlider', 'doubleSlider', 'fullWidthSlider']) {
    root.querySelectorAll(`[x-data="${attr}"]`).forEach((el) => {
      const swiper = Alpine.$data(el).slider;
      if (swiper) swiper.init();
    });
  }
}

function makeSetActiveItem() {
  return function setActiveItem() {
    const parentListItems = this.$root.closest('.list-items');
    if (!parentListItems) return;
    for (const item of parentListItems.querySelectorAll('.item')) {
      if (item !== this.$root) Alpine.$data(item).active = false;
    }
    if (!this.active) {
      const hook = typeof this.onActivate === 'function' ? this.onActivate() : null;
      if (this.initTimeout) clearTimeout(this.initTimeout);
      this.initTimeout = setTimeout(() => {
        this.$el.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'center' });
        Promise.resolve(hook).then(() => this.$nextTick(() => initSliders(this.$root)));
      }, 500);
    }
    this.active = !this.active;
  };
}

async function fetchACF(endpoint, id) {
  if (!id || !window.siteData?.home_url) return null;
  const url = new URL(`${window.siteData.home_url}/wp-json/wp/v2/${endpoint}/${id}`);
  url.searchParams.set('acf_format', 'standard');
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${res.status} ${res.url}`);
  return (await res.json()).acf;
}

Alpine.data('projectItem', () => ({
  active: false,
  setActiveItem: makeSetActiveItem(),
}));

Alpine.data('storyItem', () => ({
  active: false,
  setActiveItem: makeSetActiveItem(),
}));

Alpine.data('ideaItem', (itemID = null) => ({
  active: false,
  contents: { gallery: null, details: null },
  async onActivate() {
    if (this.contents.gallery || this.contents.details) return;
    try {
      const acf = await fetchACF('idea', itemID);
      if (acf?.gallery) this.contents.gallery = acf.gallery;
      if (acf?.details) this.contents.details = acf.details;
    } catch (_e) {}
  },
  setActiveItem: makeSetActiveItem(),
}));

Alpine.data('singleSlider', () => ({
  init() {
    this.isSingle = document.body.classList.contains('single');

    const swiper = this.$el.querySelector('.swiper');
    const pagination = this.$el.querySelector('.slider-pagination');
    const prevButton = this.$el.querySelector('.slider-prev-button');
    const nextButton = this.$el.querySelector('.slider-next-button');
    const caption = this.$el.querySelector('.slider-caption');

    if (!swiper) return;
    this.slider = new Swiper(swiper, {
      init: this.isSingle,
      modules: [Pagination, Navigation],
      pagination: {
        el: pagination,
        type: 'custom',
        renderCustom: (swiper, current, total) => `${current} of ${total}`,
      },
      navigation: {
        prevEl: prevButton,
        nextEl: nextButton,
      },
      on: {
        init: (swiper) => {
          const activeSlide = swiper.slides[swiper.activeIndex];
          if (activeSlide.querySelector('video')) {
            const video = activeSlide.querySelector('video');
            if (video.paused) {
              video.play();
            }
          }
          const captionText = activeSlide.dataset.slideCaption;
          caption.innerText = captionText ?? null;
        },
        slidesUpdated: (swiper) => {
          const activeSlide = swiper.slides[swiper.activeIndex];
          if (activeSlide.querySelector('video')) {
            const video = activeSlide.querySelector('video');
            if (video.paused) {
              video.play();
            }
          }
          const captionText = activeSlide.dataset.slideCaption;
          caption.innerText = captionText ?? null;
        },
        slideChange: (swiper) => {
          const activeSlide = swiper.slides[swiper.activeIndex];
          if (activeSlide.querySelector('video')) {
            const video = activeSlide.querySelector('video');
            if (video.paused) {
              video.play();
            }
          } else {
            for (const slide of swiper.slides) {
              if (slide.querySelector('video')) {
                const video = slide.querySelector('video');
                if (!video.paused) {
                  video.pause();
                }
              }
            }
          }

          const captionText =
            swiper.slides[swiper.activeIndex].dataset.slideCaption;
          caption.innerText = captionText ?? null;
        },
        destroy: () => {
          for (const slide of swiper.slides) {
            if (slide.querySelector('video')) {
              const video = slide.querySelector('video');
              if (!video.paused) {
                video.pause();
              }
            }
          }
        },
      },
    });
  },
  slider: null,
  isSingle: false,
}));

Alpine.data('doubleSlider', () => ({
  init() {
    this.isSingle = document.body.classList.contains('single');

    const swiper = this.$el.querySelector('.swiper');
    const pagination = this.$el.querySelector('.slider-pagination');
    const prevButton = this.$el.querySelector('.slider-prev-button');
    const nextButton = this.$el.querySelector('.slider-next-button');
    const caption = this.$el.querySelector('.slider-caption');

    if (!swiper) return;
    this.slider = new Swiper(swiper, {
      init: this.isSingle,
      modules: [Pagination, Navigation, Grid],
      slidesPerView: 1,
      grid: {
        fill: 'row',
        rows: 2,
      },
      spaceBetween: 16,
      pagination: {
        el: pagination,
        type: 'custom',
        renderCustom: (swiper, current, total) => `${current} of ${total}`,
      },
      navigation: {
        prevEl: prevButton,
        nextEl: nextButton,
      },
      on: {
        slidesUpdated: (swiper) => {
          const captionText =
            swiper.slides[swiper.activeIndex].dataset.slideCaption;
          caption.innerText = captionText ?? null;
        },
        slideChange: (swiper) => {
          const captionText =
            swiper.slides[swiper.activeIndex].dataset.slideCaption;
          caption.innerText = captionText ?? null;
        },
        destroy: () => {
          for (const slide of swiper.slides) {
            if (slide.querySelector('video')) {
              const video = slide.querySelector('video');
              if (!video.paused) {
                video.pause();
              }
            }
          }
        },
      },
      breakpoints: {
        768: {
          slidesPerView: 2,
          grid: {
            fill: 'column',
            rows: 1,
          },
          spaceBetween: 24,
        },
      },
    });
  },
  slider: null,
  isSingle: false,
}));

Alpine.data('fullWidthSlider', () => ({
  init() {
    this.isSingle = document.body.classList.contains('single');

    const swiper = this.$el.querySelector('.swiper');
    const pagination = this.$el.querySelector('.slider-pagination');
    const prevButton = this.$el.querySelector('.slider-prev-button');
    const nextButton = this.$el.querySelector('.slider-next-button');
    const caption = this.$el.querySelector('.slider-caption');

    if (!swiper) return;
    this.slider = new Swiper(swiper, {
      init: this.isSingle,
      modules: [Pagination, Navigation],
      pagination: {
        el: pagination,
        type: 'custom',
        renderCustom: (swiper, current, total) => `${current} of ${total}`,
      },
      navigation: {
        prevEl: prevButton,
        nextEl: nextButton,
      },
      on: {
        slidesUpdated: (swiper) => {
          const captionText =
            swiper.slides[swiper.activeIndex].dataset.slideCaption;
          caption.innerText = captionText ?? null;
        },
        slideChange: (swiper) => {
          const captionText =
            swiper.slides[swiper.activeIndex].dataset.slideCaption;
          caption.innerText = captionText ?? null;
        },
        destroy: () => {
          for (const slide of swiper.slides) {
            if (slide.querySelector('video')) {
              const video = slide.querySelector('video');
              if (!video.paused) {
                video.pause();
              }
            }
          }
        },
      },
    });
  },
  slider: null,
  isSingle: false,
}));

// About Slider
Alpine.data('aboutSlider', () => ({
  init() {
    const swiper = this.$el.querySelector('.swiper');

    if (!swiper) return;
    this.slider = new Swiper(swiper, {
      modules: [Pagination, Navigation],
      pagination: {
        el: this.$el.querySelector('.slider-pagination'),
        type: 'custom',
        renderCustom: function (swiper, current, total) {
          return `${current} of ${total}`;
        },
      },
      navigation: {
        prevEl: this.$el.querySelector('.slider-prev-button'),
        nextEl: this.$el.querySelector('.slider-next-button'),
      },
    });
  },
  slider: null,
}));

// Studio
Alpine.data('studioSlider', () => ({
  init() {
    const swiper = this.$el.querySelector('.swiper');

    if (!swiper) return;
    this.slider = new Swiper(swiper, {
      modules: [Pagination, Navigation],
      pagination: {
        el: this.$el.querySelector('.swiper-pagination'),
        type: 'custom',
        renderCustom: function (swiper, current, total) {
          return `${current} of ${total}`;
        },
      },
      navigation: {
        prevEl: this.$el.querySelector('.swiper-prev-button'),
        nextEl: this.$el.querySelector('.swiper-next-button'),
      },
    });
  },
  slider: null,
}));

// Screensaver
Alpine.data('screensaver', () => ({
  init() {
    this.screensaverInit();
  },
  screensaverTimer: null,
  active: false,
  screensaverInit() {
    this.screensaverTimer = setTimeout(() => {
      this.active = true;
    }, 180000);
  },
  screensaverTerminate() {
    clearTimeout(this.screensaverTimer);
    this.active = false;
    this.screensaverInit();
  },
}));

// List Line Animation
const listItems = document.querySelector('.list-items');
const listLines = document.querySelector('.list-lines');
const lastLine = document.querySelector('.list-lines > div:last-child');

if (lastLine) {
  lastLine.addEventListener('transitionend', () => {
    listItems.classList.remove('opacity-0');
    setTimeout(() => {
      listLines.classList.add('hidden');
    }, 1000);
  });
}

Alpine.start();
